'use client'
import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { Listbox } from '@headlessui/react'
import { RiArrowDropDownLine } from 'react-icons/ri'
import { FaCheck } from "react-icons/fa";
import tailwindConfig from '@tailwind.config'

import { getSearchParams, updateCategoriesParams, removeSearchParam } from "@utils";
import { categoryTypes } from '@constants/homeCategoryTypes';

const PARAM_KEY = 'categories';

const CategoriesFilter = ({ title, lang, dictionary }) => {
  const router = useRouter();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const lastClickedRef = useRef(null);

  useEffect(() => {
    const categoriesValue = getSearchParams(PARAM_KEY);
    if (categoriesValue) {
      const fromUrl = categoriesValue.split(',').filter(Boolean);
      setSelectedCategories(fromUrl.length ? fromUrl : []);
    }
  }, []);

  const handleChange = (values) => {
    const clicked = lastClickedRef.current;
    // reset ref so it doesn't affect future changes
    lastClickedRef.current = null;

    let normalized;
    if (clicked === 'all') {
      // user explicitly clicked "all"
      if (selectedCategories.includes('all')) {
        // was selected -> user is deselecting it
        normalized = []; 
      } else {
        // select only all
        normalized = ['all'];
      }
    } else {
      // user clicked some other category -> never keep 'all' with others
      normalized = values.filter(v => v !== 'all');
    }

    setSelectedCategories(normalized);

    // Update URL: remove param for empty or "all", otherwise set it
    if (normalized.length === 0 || normalized.includes('all')) {
      router.push(removeSearchParam(PARAM_KEY));
    } else {
      router.push(updateCategoriesParams(normalized.join(',')));
    }
  };

  const getTitleByValue = (value) => {
    const c = categoryTypes[lang].find(cat => cat.value === value);
    return c ? c.title : value;
  };

  return (
    <div className="w-full relative">
      <label className='text-maincolor text-sm pb-1'>{dictionary.filter.category}:</label>
      <Listbox value={selectedCategories} onChange={handleChange} multiple>
        <div className="w-full">
          <div className='z-1 w-full mt-1 cursor-default overflow-hidden rounded-lg bg-white text-left border-[1px] border-forth text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 sm:text-sm'>
            <Listbox.Button className='w-full relative border-none py-2 pl-3 pr-10 text-sm flex items-center leading-5 text-gray-900 focus:ring-0'>
              {selectedCategories.length === 0 || selectedCategories.includes('all')
                ? categoryTypes[lang][0].title
                : selectedCategories.map(getTitleByValue).join(', ')
              }
              <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                <RiArrowDropDownLine
                  color={tailwindConfig.theme.extend.colors.secondary}
                  size={28}
                />
              </span>
            </Listbox.Button>
          </div>

          <Listbox.Options className='z-50 left-0 right-0 absolute bg-white h-auto overflow-y-auto rounded-md py-1'>
            {categoryTypes[lang].map((category) => (
              <Listbox.Option
                key={category.value}
                value={category.value}
              >
                {({ active }) => (
                  // capture which option was clicked before HeadlessUI updates selection
                  <span
                    onMouseDown={() => { lastClickedRef.current = category.value }}
                    className={`p-2 cursor-pointer flex flex-row items-center text-sm ${active ? 'bg-maincolor text-light' : ''}`}
                  >
                    {selectedCategories.includes(category.value) && <FaCheck className='mr-2' />}
                    {category.title}
                  </span>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
}

export default CategoriesFilter;
